import { useState } from 'react';
import { BookOpen, Plus, Search, Archive, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { SidePanel } from '@/components/ui/SidePanel';
import { Markdown } from '@/components/ui/Markdown';
import { Table, Th, Td, Tr } from '@/components/ui/Table';
import { EmptyState, ErrorState } from '@/components/ui/States';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { confirm } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';
import { parseTags } from '@/lib/tags';
import { useIsStaff } from '@/app/store';
import { useAsync } from '@/lib/useAsync';
import { timeAgo } from '@/lib/timeAgo';
import { api, ApiError, type KBEntry, type KnowledgeEntryScope } from '@/lib/api';
import { useCompanyCtx } from './CompanyLayout';

const SCOPES: { key: 'ALL' | KnowledgeEntryScope; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'GLOBAL', label: 'Firm' },
  { key: 'COMPANY', label: 'Company' },
  { key: 'WORKSPACE', label: 'Engagement' },
  { key: 'PROJECT', label: 'Workstream' },
];
const scopeLabel = (s: KnowledgeEntryScope) => SCOPES.find((x) => x.key === s)?.label ?? s;

/** The knowledge base browser: every entry in the cascade under this company. */
export function CompanyKnowledgeTab() {
  const { company } = useCompanyCtx();
  const isStaff = useIsStaff();
  const entries = useAsync(() => api.getKnowledgeBase(company.id), [company.id]);
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<'ALL' | KnowledgeEntryScope>('ALL');
  const [tags, setTags] = useState<Set<string>>(new Set());
  const [readerId, setReaderId] = useState<string | null>(null);
  // null = closed; { entry: null } = new company learning; { entry } = edit.
  const [editor, setEditor] = useState<{ entry: KBEntry | null } | null>(null);

  if (entries.status === 'loading') return <SkeletonRows rows={5} />;
  if (entries.status === 'error') return <ErrorState error={entries.error} onRetry={entries.reload} />;

  const q = query.trim().toLowerCase();
  const searched = entries.data.filter(
    (e) =>
      !q ||
      e.title.toLowerCase().includes(q) ||
      e.body.toLowerCase().includes(q) ||
      e.tags.some((t) => t.toLowerCase().includes(q)),
  );
  // Selected tags narrow: an entry must carry every selected tag.
  const tagged = searched.filter((e) => [...tags].every((t) => e.tags.includes(t)));
  const visible = tagged.filter((e) => scope === 'ALL' || e.scope === scope);
  const reader = entries.data.find((e) => e.id === readerId) ?? null;

  const scopeCount = (key: 'ALL' | KnowledgeEntryScope) =>
    key === 'ALL' ? tagged.length : tagged.filter((e) => e.scope === key).length;

  // The tag rail reflects the search but not the tag selection, so multi-select stays possible.
  const tagCounts = new Map<string, number>();
  for (const e of searched) for (const t of e.tags) tagCounts.set(t, (tagCounts.get(t) ?? 0) + 1);
  const topTags = [...tagCounts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, 15);

  const toggleTag = (t: string) =>
    setTags((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold">
          Knowledge base{' '}
          <span className="text-xs font-normal text-fg-subtle">
            {visible.length} of {entries.data.length}
          </span>
        </h2>
        <div className="relative ml-2">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title, body, tags"
            className="w-64 pl-8"
          />
        </div>
        {isStaff && (
          <Button size="sm" className="ml-auto" onClick={() => setEditor({ entry: null })}>
            <Plus className="h-4 w-4" /> Add company learning
          </Button>
        )}
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        {SCOPES.map((s) => (
          <button
            key={s.key}
            onClick={() => setScope(s.key)}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition',
              scope === s.key ? 'border-primary bg-primary-muted text-primary' : 'border-border text-fg-muted hover:text-fg',
            )}
          >
            {s.label} <span className="tabular-nums opacity-70">{scopeCount(s.key)}</span>
          </button>
        ))}
      </div>

      {topTags.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-fg-subtle">Tags:</span>
          {topTags.map(([t, n]) => (
            <button
              key={t}
              onClick={() => toggleTag(t)}
              className={cn(
                'rounded-full border px-2 py-0.5 text-xs transition',
                tags.has(t) ? 'border-accent bg-accent/10 font-medium text-accent' : 'border-border text-fg-muted hover:text-fg',
              )}
            >
              {t} <span className="tabular-nums opacity-70">{n}</span>
            </button>
          ))}
          {tags.size > 0 && (
            <button onClick={() => setTags(new Set())} className="text-xs text-fg-subtle underline hover:text-fg">
              clear
            </button>
          )}
        </div>
      )}

      {visible.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title={q || tags.size > 0 || scope !== 'ALL' ? 'No matches' : 'No knowledge yet'}
          description={
            q || tags.size > 0 || scope !== 'ALL'
              ? 'Try widening the search, scope, or tag filters.'
              : 'Approved suggestions and staff learnings across the cascade appear here.'
          }
        />
      ) : (
        <Table>
          <thead className="bg-surface-2">
            <tr>
              <Th>Title</Th>
              <Th>Location</Th>
              <Th>Tags</Th>
              <Th>Origin</Th>
              <Th>Updated</Th>
            </tr>
          </thead>
          <tbody>
            {visible.map((e) => (
              <Tr key={e.id} onClick={() => setReaderId(e.id)} className="cursor-pointer hover:bg-surface-2/50">
                <Td className="max-w-80">
                  <span className="block truncate font-medium text-fg">{e.title}</span>
                </Td>
                <Td className="whitespace-nowrap text-fg-muted">{e.location.label}</Td>
                <Td className="max-w-56">
                  <span className="flex flex-wrap gap-1">
                    {e.tags.map((t) => (
                      <Badge key={t} tone="neutral">{t}</Badge>
                    ))}
                  </span>
                </Td>
                <Td className="whitespace-nowrap">
                  <Badge tone={e.origin === 'AGENT_APPROVED' ? 'primary' : 'neutral'}>
                    {e.origin === 'AGENT_APPROVED' ? 'agent · approved' : 'staff'}
                  </Badge>
                </Td>
                <Td className="whitespace-nowrap text-fg-muted">{timeAgo(e.updatedAt)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      {reader && (
        <KBReader
          entry={reader}
          isStaff={isStaff}
          onClose={() => setReaderId(null)}
          onEdit={() => {
            setReaderId(null);
            setEditor({ entry: reader });
          }}
          onChanged={() => {
            setReaderId(null);
            entries.reload();
          }}
        />
      )}
      {editor && (
        <KBEditorPanel
          companyId={company.id}
          entry={editor.entry}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            entries.reload();
          }}
        />
      )}
    </>
  );
}

function KBReader({
  entry,
  isStaff,
  onClose,
  onEdit,
  onChanged,
}: {
  entry: KBEntry;
  isStaff: boolean;
  onClose: () => void;
  onEdit: () => void;
  onChanged: () => void;
}) {
  async function archive() {
    const ok = await confirm({
      title: `Archive “${entry.title}”?`,
      description: 'Agents stop applying it on their next run. It can be restored from its home tab.',
    });
    if (!ok) return;
    try {
      await api.updateKnowledge(entry.id, { archived: true });
      toast.success('Archived');
      onChanged();
    } catch (err) {
      toast.danger('Could not archive', err instanceof ApiError ? err.message : undefined);
    }
  }

  return (
    <SidePanel
      title={entry.title}
      subtitle={
        <span className="flex flex-wrap items-center gap-2">
          <Badge tone="primary">{scopeLabel(entry.scope)}</Badge>
          <span className="text-xs text-fg-subtle">{entry.location.label}</span>
          <span className="text-xs text-fg-subtle">Updated {timeAgo(entry.updatedAt)}</span>
        </span>
      }
      onClose={onClose}
      footer={
        isStaff ? (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={archive}>
              <Archive className="h-3.5 w-3.5" /> Archive
            </Button>
            <Button size="sm" onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {entry.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {entry.tags.map((t) => (
              <Badge key={t} tone="neutral">{t}</Badge>
            ))}
          </div>
        )}
        <Markdown className="text-sm">{entry.body}</Markdown>
      </div>
    </SidePanel>
  );
}

function KBEditorPanel({
  companyId,
  entry,
  onClose,
  onSaved,
}: {
  companyId: string;
  entry: KBEntry | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(entry?.title ?? '');
  const [body, setBody] = useState(entry?.body ?? '');
  const [tagsRaw, setTagsRaw] = useState(entry?.tags.join(', ') ?? '');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const payload = { title: title.trim(), body, tags: parseTags(tagsRaw) };
    try {
      if (entry) await api.updateKnowledge(entry.id, payload);
      else await api.createCompanyLearning(companyId, payload);
      toast.success(entry ? 'Saved' : 'Learning added');
      onSaved();
    } catch (err) {
      toast.danger('Could not save', err instanceof ApiError ? err.message : undefined);
      setBusy(false);
    }
  }

  return (
    <SidePanel
      title={entry ? 'Edit entry' : 'New company learning'}
      subtitle={<Badge tone="primary">{entry ? scopeLabel(entry.scope) : 'Company'}</Badge>}
      onClose={onClose}
    >
      <form className="space-y-4" onSubmit={submit}>
        <Field label="Title" htmlFor="kb-title">
          <Input id="kb-title" value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </Field>
        <Field label="Tags" htmlFor="kb-tags" hint="Comma-separated, up to 12. Used for browsing and filtering.">
          <Input id="kb-tags" value={tagsRaw} onChange={(e) => setTagsRaw(e.target.value)} placeholder="vip, payments" />
        </Field>
        <Field label="Body" htmlFor="kb-body" hint="Markdown. Agents read this verbatim on every run in scope.">
          <textarea
            id="kb-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={14}
            required
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm leading-relaxed text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
        </Field>
        <Button type="submit" disabled={busy || !title.trim() || !body.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </form>
    </SidePanel>
  );
}
